/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  SLRLabelIcon,
  SLRMediaSize,
  SLRMobileNavBar,
  SLRNavBar,
  SLRProfileDropdown,
  getIcon,
  useAuth,
  useGetMediaItem,
  useWindowSize
} from '@SLR/shared-library';
import {
  ADMIN_ONBOARDING_REQUESTS_PATH,
  DEFAULT_PROFILE_IMAGE_PATH,
  MOBILE_VIEW_WIDTH,
  ORGANIZATIONS_PATH,
  PROFILE_PATH,
  SOLUTIONS_PATH
} from 'configs';
import { useGetCurrentUserProfile } from 'hooks';
import { FC, useMemo } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { AppLogo } from 'assets';
import './NavBar.scss';

export const NavBar: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isAuthenticated, logout, hasClientRole } = useAuth();
  const isOnboardingAdmin = hasClientRole('onboarding', 'mdl');

  const { isMobileView, width } = useWindowSize(
    isOnboardingAdmin ? 900 : MOBILE_VIEW_WIDTH
  );

  const { data: user, isPending: isUserLoading } = useGetCurrentUserProfile();
  const { data: profilePicture, isFetching: isUserPictureLoading } =
    useGetMediaItem(user?.pictureId, SLRMediaSize.Small);

  const isLoading = isAuthenticated && (isUserLoading || isUserPictureLoading);

  const navigationItems = useMemo(() => {
    const items = [
      {
        to: PROFILE_PATH,
        label: t('profile'),
        icon: getIcon('fal', 'user')
      },
      {
        to: ORGANIZATIONS_PATH,
        label: t('organizations'),
        icon: getIcon('fal', 'users')
      },
      {
        to: SOLUTIONS_PATH,
        label: t('solutions'),
        icon: getIcon('fal', 'grid-2')
      }
    ];

    if (isOnboardingAdmin) {
      items.push({
        to: ADMIN_ONBOARDING_REQUESTS_PATH,
        label: t('requests'),
        icon: getIcon('fal', 'comments-question-check')
      });
    }
    return items;
  }, [isOnboardingAdmin, t]);

  return (
    <>
      <SLRNavBar
        appLogo={<AppLogo height={48} width={48} />}
        appName={t('navBar.appName')}
        appSubtitle={t('navBar.account')}
        appURL={isAuthenticated ? PROFILE_PATH : window.location.href}
        navItems={
          isMobileView || !isAuthenticated ? undefined : navigationItems
        }
        navItemsOptions={{
          useIncludesForIsActive: true
        }}
        showToggle={!isMobileView}
        profileDropdown={
          isAuthenticated ? (
            <SLRProfileDropdown
              keepNamesVisible={width > 450}
              isLoading={isLoading}
              lastName={user?.lastName}
              firstName={user?.firstName}
              profilePicture={profilePicture?.url}
              profilePictureFallback={DEFAULT_PROFILE_IMAGE_PATH}
            >
              <NavDropdown.Item onClick={() => logout()}>
                <SLRLabelIcon
                  icon={getIcon('fal', 'sign-out-alt')}
                  label={t('navBar.logout')}
                />
              </NavDropdown.Item>
            </SLRProfileDropdown>
          ) : undefined
        }
        currentPath={pathname}
      />
      {isAuthenticated && isMobileView && (
        <SLRMobileNavBar
          navItems={navigationItems}
          navItemsOptions={{
            isIconVisible: true,
            useIncludesForIsActive: true
          }}
          currentPath={pathname}
        />
      )}
    </>
  );
};
